import {
	getRuntimeDisabledPages,
	getRuntimeEnabledModules,
	getRuntimeModuleEnabledPages
} from './tenantConfig';

/**
 * Menu Filter Utility
 * File: menuFilter.js
 * Path: ananunadm/src/helpers/menuFilter.js
 * 
 * Filters menu items based on environment configuration
 * Supports both blacklist (disable specific) and whitelist (enable specific) modes
 */

/**
 * Get disabled pages from environment (BLACKLIST mode)
 * Format: REACT_APP_DISABLED_PAGES=payroll,gpu,cpu,ram
 * @returns {string[]} Array of disabled page permission models
 */
const getDisabledPages = () => {
	const disabled = process.env.REACT_APP_DISABLED_PAGES || '';
	const buildPages = disabled.split(',').map((p) => p.trim()).filter(Boolean);
	return getRuntimeDisabledPages(buildPages);
};

/**
 * Get enabled pages for a specific module (WHITELIST mode)
 * Format: REACT_APP_HR_ENABLED_PAGES=user,department,position
 * @param {string} moduleName - The module name (hr, it, finance, etc.)
 * @returns {string[]|null} Array of enabled pages or null if not using whitelist
 */
const getModuleEnabledPages = (moduleName) => {
	const envKey = `REACT_APP_${moduleName.toUpperCase().replace(/[^A-Z0-9]/g, "_")}_ENABLED_PAGES`;
	const enabled = process.env[envKey] || '';
	const buildPages = enabled ? enabled.split(',').map((p) => p.trim()).filter(Boolean) : null;
	return getRuntimeModuleEnabledPages(moduleName, buildPages);
};

/**
 * Get enabled modules from environment
 * Format: REACT_APP_APIT_ENABLED_MODULES=chat,cm,crm,hr,it,office-map
 * @returns {string[]} Array of enabled module names
 */
const getEnabledModules = () => {
	const modules = process.env.REACT_APP_APIT_ENABLED_MODULES || '';
	const buildModules = modules
		? modules
				.split(',')
				.map((m) => m.trim().toLowerCase())
				.filter(Boolean)
				.map((m) => (m === 'meeting' ? 'meetings' : m))
		: ['all'];
	return getRuntimeEnabledModules(buildModules);
};

/**
 * Check if a specific page is enabled
 * @param {string} permissionModel - The permission model name
 * @param {string|null} moduleName - Optional module name for whitelist checking
 * @returns {boolean}
 */
const isPageEnabled = (permissionModel, moduleName = null) => {
	const disabledPages = getDisabledPages();
	
	// Check blacklist
	if (disabledPages.includes(permissionModel)) {
		return false;
	}
	
	// Check module whitelist if exists
	if (moduleName) {
		const modulePages = getModuleEnabledPages(moduleName);
		if (modulePages !== null) {
			return modulePages.includes(permissionModel);
		}
	}
	
	return true;
};

/**
 * Check if a specific module is enabled
 * @param {string} moduleName - The module name
 * @returns {boolean}
 */
export const isModuleEnabled = (moduleName) => {
	const enabledModules = getEnabledModules();
	return enabledModules.includes('all') || enabledModules.includes(moduleName.toLowerCase());
};

/**
 * Filter a single menu item and its children
 * @param {object} item - Menu item object
 * @param {string[]} disabledPages - Array of disabled page permission models
 * @param {string[]|null} moduleEnabledPages - Whitelist for specific module or null
 * @returns {object|null} Filtered menu item or null if should be hidden
 */
const filterMenuItem = (item, disabledPages, moduleEnabledPages) => {
	// Check if this item is disabled by blacklist
	if (item.permissionModel && disabledPages.includes(item.permissionModel)) {
		return null;
	}

	// Check module whitelist if exists
	if (moduleEnabledPages && item.permissionModel) {
		if (!moduleEnabledPages.includes(item.permissionModel)) {
			return null;
		}
	}

	// Process subMenu if exists
	if (item.subMenu && item.subMenu.length > 0) {
		const filteredSubMenu = item.subMenu
			.map((subItem) => filterMenuItem(subItem, disabledPages, moduleEnabledPages))
			.filter(Boolean);

		// If all sub-items are filtered out, hide the parent too
		if (filteredSubMenu.length === 0) {
			return null;
		}

		return { ...item, subMenu: filteredSubMenu };
	}

	return item;
};

/**
 * Filter a menu section and its items
 * @param {object} section - Menu section object
 * @param {string[]} disabledPages - Array of disabled page permission models
 * @returns {object|null} Filtered section or null if should be hidden
 */
const filterMenuSection = (section, disabledPages) => {
	const moduleName = section.route;
	const moduleEnabledPages = getModuleEnabledPages(moduleName);

	// If section has no menu items, return as-is
	if (!section.menu || section.menu.length === 0) {
		return section;
	}

	// Filter menu items
	const filteredMenu = section.menu
		.map((item) => filterMenuItem(item, disabledPages, moduleEnabledPages))
		.filter(Boolean);

	// If all menu items are filtered out, hide the entire section
	if (filteredMenu.length === 0) {
		return null;
	}

	return { ...section, menu: filteredMenu };
};

/**
 * Main filter function - filters entire menu configuration
 * @param {object[]} menuConfig - Array of menu section objects
 * @returns {object[]} Filtered menu configuration
 */
export const filterMenu = (menuConfig) => {
	const enabledModules = getEnabledModules();
	const disabledPages = getDisabledPages();

	return menuConfig
		// First filter by enabled modules
		.filter((section) => {
			if (!section.route) return true;
			return enabledModules.includes('all') || enabledModules.includes(section.route.toLowerCase());
		})
		// Then filter individual pages within each section
		.map((section) => filterMenuSection(section, disabledPages))
		.filter(Boolean);
};

/**
 * Get configuration summary for debugging
 * @returns {object} Configuration summary
 */
export const getConfigSummary = () => {
	const enabledModules = getEnabledModules();
	const disabledPages = getDisabledPages();
	const moduleWhitelists = {};
	
	['hr', 'it', 'finance', 'ecommerce', 'crm', 'cm', 'todo', 'radio', 'media', 'chat', 'website-chat', 'meetings', 'ai', 'calendar', 'office-map', 'security', 'system', 'ticket', 'project', 'knowledge'].forEach((mod) => {
		const whitelist = getModuleEnabledPages(mod);
		if (whitelist) {
			moduleWhitelists[mod] = whitelist;
		}
	});

	return {
		enabledModules,
		disabledPages,
		moduleWhitelists,
		mode: disabledPages.length > 0 ? 'blacklist' : (Object.keys(moduleWhitelists).length > 0 ? 'whitelist' : 'all'),
	};
};


// Default export
export default {
	filterMenu,
	isPageEnabled,
	isModuleEnabled,
	getDisabledPages,
	getEnabledModules,
	getModuleEnabledPages,
	getConfigSummary,
};
